/*
 * SearchWidget.tsx
 *
 * Created by Dr. Maximillian Dornseif 2021-11-29 in huWaWi3 24.6.0
 * Copyright (c) 2021 HUDORA GmbH
 */

import { useQuery } from '@apollo/client'
import { Callout, Spinner, SpinnerSize, Text, mergeStyleSets } from '@fluentui/react'
import { LinkRender, getLink } from './lib/hd-link'
import { useDebounce } from '@hudora/hd-react-components'
import gql from 'graphql-tag'
import React, { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'wouter'
import { navigate } from 'wouter/use-location'

import { useHotkey } from './lib/hooks/useHotkey'
import { fuzzaldrinHighlight, fuzzaldrinSort } from './lib/util/fuzzaldrin-react'

export function SearchWidget(props) {
  const root = document.getElementById('hd-search-widget-root')

  if (!root) {
    return <>{props.children}</>
  }
  return (
    <>
      {createPortal(<SearchWidgetInner />, root)}
      {props.children}
    </>
  )
}
const SearchWidgetInner = () => {
  const elementRef = useHotkey('f', 'Beleg№-Suche')
  const [term, setTerm] = useState<string>('')
  const debouncedTerm = useDebounce(term, 250)
  const { loading, data } = useQuery(SEARCH_QUERY, {
    variables: { term: debouncedTerm },
    skip: !debouncedTerm || debouncedTerm?.length < 0,
  })
  const clearResults = useCallback(() => setTerm(null), [setTerm])
  const pressEnter = useCallback(
    (event) => {
      for (const result of data?.search || []) {
        if (result.designator === term) {
          const href = getLink(result.designator)
          if (href) {
            navigate(href)
            setTerm('')
            event.preventDefault()
            return
          }
        }
      }
      for (const result of data?.search || []) {
        const href = getLink(result.designator)
        if (href) {
          navigate(href)
        }
      }
      setTerm('')
      event.preventDefault()
    },
    [setTerm, data]
  )

  return (
    <form id="belegsuche" role="search" onSubmit={pressEnter}>
      <div>
        <input
          ref={elementRef}
          style={{
            width: '10em',
          }}
          type="search"
          className="form-control"
          placeholder="Suche: №"
          maxLength={200}
          autoComplete="off"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          name="q"
          id="q"
        />
        {loading ? (
          <div
            style={{
              width: '8.5em',
              position: 'absolute',
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Spinner size={SpinnerSize.xSmall} />
          </div>
        ) : null}
        <SearchResults results={data?.search} term={debouncedTerm} clearResults={clearResults} />
      </div>
    </form>
  )
}
const SEARCH_QUERY = gql`
  query FulltextSearchQuery($term: String!) {
    search(term: $term) {
      designator
      id
      istAktiv
      matches
      title
      url
      schema
    }
  }
`
function SearchResults({ term, results, clearResults }) {
  const suggestions = fuzzaldrinSort(term, results)
  const styles = mergeStyleSets({
    callout: {
      minWidth: 320,
      padding: '20px 24px',
    },
  })
  return (
    <Callout
      className={styles.callout}
      // ariaLabelledBy={labelId}
      // ariaDescribedBy={descriptionId}
      // dismissOnTargetClick={true}
      hidden={!results}
      // onDismiss={clearResults}
      target={`#hd-search-widget-root`}
    >
      <div onClick={clearResults}>
        <Text block>
          Suche nach <b>{term}</b>
        </Text>
        <ul>
          {suggestions.map((x) => (
            <li key={x.designator} style={{ textDecoration: x.istAktiv === false ? 'line-through' : 'none' }}>
              <LinkRender onClick={clearResults} value={x.designator}>
                {' '}
                {fuzzaldrinHighlight(x?.title || '', term)}
              </LinkRender>{' '}
              ({x.schema})
            </li>
          ))}
        </ul>
        <Text>
          Kein gutes Ergebnis? Versuchen Sie die{' '}
          <Link onClick={clearResults} href="/">
            Such-Seite
          </Link>
          .
        </Text>
      </div>
    </Callout>
  )
}
